import { useEffect, useState } from "react";
import { Save, ExternalLink, Plus, Trash2, Image as ImageIcon, Instagram, Facebook, Globe } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loading } from "@/components/dash/ui";
import { ImageUpload } from "@/components/dash/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function Section({ title, desc, children }) {
  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-4">
      <div>
        <h2 className="font-heading text-xl font-medium">{title}</h2>
        {desc && <p className="text-sm text-muted-foreground mt-0.5">{desc}</p>}
      </div>
      {children}
    </div>
  );
}

export default function WebsiteBuilder() {
  const [r, setR] = useState(null);
  const [saving, setSaving] = useState(false);
  const [slots, setSlots] = useState([]);

  useEffect(() => {
    api.get("/restaurant").then((res) => {
      setR({ ...res.data, site_content: res.data.site_content || {} });
      setSlots((res.data.site_content?.gallery || []).map((url) => ({ id: crypto.randomUUID(), url })));
    });
  }, []);

  const set = (k) => (e) => setR({ ...r, [k]: e.target.value });
  const setVal = (k, v) => setR({ ...r, [k]: v });
  const setSC = (k, v) => setR({ ...r, site_content: { ...r.site_content, [k]: v } });

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const gallery = slots.map((s) => s.url).filter(Boolean);
      const { data } = await api.put("/restaurant", {
        name: r.name, cuisine: r.cuisine, description: r.description,
        phone: r.phone, email: r.email, address: r.address, hours: r.hours,
        logo_url: r.logo_url, cover_image: r.cover_image,
        brand_color: r.brand_color, accent_color: r.accent_color, theme: r.theme,
        site_content: { ...r.site_content, gallery },
      });
      setR({ ...data, site_content: data.site_content || {} });
      setSlots((data.site_content?.gallery || []).map((url) => ({ id: crypto.randomUUID(), url })));
      toast.success("Website saved");
    } catch (err) { toast.error(apiErr(err)); }
    setSaving(false);
  };

  if (!r) return <Loading />;
  const sc = r.site_content || {};
  const siteUrl = `${window.location.origin}/m/${r.slug}`;

  return (
    <div data-testid="website-builder-page">
      <PageHeader title="Website Builder" subtitle="Customize your public website — text, images, branding and links."
        action={
          <div className="flex items-center gap-2">
            <a href={siteUrl} target="_blank" rel="noreferrer" data-testid="wb-view-site"
              className="text-sm text-primary hover:underline flex items-center gap-1">
              View live site <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <Button data-testid="wb-save" onClick={save} disabled={saving} className="bg-primary hover:bg-[#B35A42] text-primary-foreground">
              <Save className="h-4 w-4 mr-2" /> {saving ? "Saving..." : "Save website"}
            </Button>
          </div>
        } />

      <div className="space-y-6 max-w-3xl">
        <Section title="Branding" desc="Your logo, theme and colors define the look of your public site.">
          <div className="grid sm:grid-cols-2 gap-5">
            <ImageUpload label="Logo" value={r.logo_url} onChange={(v) => setVal("logo_url", v)} testid="wb-logo" aspect="aspect-[3/1]" />
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Theme</Label>
                <Select value={r.theme || "modern"} onValueChange={(v) => setVal("theme", v)}>
                  <SelectTrigger data-testid="wb-theme"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="modern">Modern (image hero)</SelectItem>
                    <SelectItem value="minimal">Minimal (clean, centered)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Brand color</Label>
                  <div className="flex gap-2 items-center">
                    <input type="color" value={r.brand_color || "#16323B"} onChange={set("brand_color")} className="h-9 w-10 rounded border border-border bg-card cursor-pointer" data-testid="wb-brand-color" />
                    <Input value={r.brand_color || ""} onChange={set("brand_color")} placeholder="#16323B" />
                  </div>
                </div>
                <div className="space-y-1.5"><Label>Accent color</Label>
                  <div className="flex gap-2 items-center">
                    <input type="color" value={r.accent_color || "#C7A867"} onChange={set("accent_color")} className="h-9 w-10 rounded border border-border bg-card cursor-pointer" data-testid="wb-accent-color" />
                    <Input value={r.accent_color || ""} onChange={set("accent_color")} placeholder="#C7A867" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>

        <Section title="Hero" desc="The first thing guests see. Leave text blank to use your restaurant name and description.">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Hero title</Label>
                <Input data-testid="wb-hero-title" value={sc.hero_title || ""} onChange={(e) => setSC("hero_title", e.target.value)} placeholder={r.name} /></div>
              <div className="space-y-1.5"><Label>Hero subtitle</Label>
                <Textarea data-testid="wb-hero-subtitle" value={sc.hero_subtitle || ""} onChange={(e) => setSC("hero_subtitle", e.target.value)} placeholder={r.description} /></div>
            </div>
            <ImageUpload label="Hero / cover image" value={r.cover_image} onChange={(v) => setVal("cover_image", v)} testid="wb-cover" />
          </div>
        </Section>

        <Section title="About" desc="Tell your story. This appears as a section on your site.">
          <div className="space-y-1.5"><Label>Section title</Label>
            <Input data-testid="wb-about-title" value={sc.about_title || ""} onChange={(e) => setSC("about_title", e.target.value)} placeholder="Our Story" /></div>
          <div className="space-y-1.5"><Label>About text</Label>
            <Textarea data-testid="wb-about-text" rows={5} value={sc.about_text || ""} onChange={(e) => setSC("about_text", e.target.value)} placeholder="Share what makes your restaurant special..." /></div>
        </Section>

        <Section title="Gallery" desc="Showcase your dishes and space. Add up to 9 images.">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {slots.map((slot, i) => (
              <div key={slot.id} className="relative" data-testid={`wb-gallery-${i}`}>
                <ImageUpload value={slot.url} onChange={(v) => setSlots(slots.map((s) => s.id === slot.id ? { ...s, url: v } : s))} testid={`wb-gallery-img-${i}`} aspect="aspect-square" />
                <button type="button" onClick={() => setSlots(slots.filter((s) => s.id !== slot.id))} data-testid={`wb-gallery-del-${i}`}
                  className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-white flex items-center justify-center shadow">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            {slots.length < 9 && (
              <button type="button" onClick={() => setSlots([...slots, { id: crypto.randomUUID(), url: "" }])} data-testid="wb-gallery-add"
                className="aspect-square rounded-lg border border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-foreground hover:border-primary transition-colors">
                <Plus className="h-5 w-5" /> <span className="text-xs">Add image</span>
              </button>
            )}
          </div>
        </Section>

        <Section title="Contact & hours" desc="How guests reach you. Shown on your public site.">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Phone</Label>
              <Input value={r.phone || ""} onChange={set("phone")} /></div>
            <div className="space-y-1.5"><Label>Email</Label>
              <Input value={r.email || ""} onChange={set("email")} /></div>
          </div>
          <div className="space-y-1.5"><Label>Address</Label>
            <Input value={r.address || ""} onChange={set("address")} /></div>
          <div className="space-y-1.5"><Label>Opening hours</Label>
            <Input value={r.hours || ""} onChange={set("hours")} placeholder="Mon–Sat 11am – 9pm · Sun 12pm – 8pm" /></div>
        </Section>

        <Section title="Social links" desc="Connect your social profiles in the site footer.">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Instagram className="h-4 w-4 text-muted-foreground shrink-0" />
              <Input data-testid="wb-instagram" value={sc.instagram || ""} onChange={(e) => setSC("instagram", e.target.value)} placeholder="https://instagram.com/yourhandle" />
            </div>
            <div className="flex items-center gap-2">
              <Facebook className="h-4 w-4 text-muted-foreground shrink-0" />
              <Input data-testid="wb-facebook" value={sc.facebook || ""} onChange={(e) => setSC("facebook", e.target.value)} placeholder="https://facebook.com/yourpage" />
            </div>
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
              <Input data-testid="wb-website" value={sc.website_url || ""} onChange={(e) => setSC("website_url", e.target.value)} placeholder="https://yourwebsite.com" />
            </div>
          </div>
        </Section>

        <div className="flex justify-end">
          <Button data-testid="wb-save-bottom" onClick={save} disabled={saving} className="bg-primary hover:bg-[#B35A42] text-primary-foreground">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Saving..." : "Save website"}
          </Button>
        </div>
      </div>
    </div>
  );
}
